import { Box, Flex, Heading, Icon, Text, VStack } from '@chakra-ui/react';
import { formatPageList } from '@common/utils/validation';
import { TriangleAlertIcon } from 'lucide-react';
import { FC, KeyboardEvent } from 'react';
import { ComplianceSuggestion } from './types';

interface DocumentSuggestionsListProps {
  suggestions: ComplianceSuggestion[];
  /**
   * Called with a row's field ids when the row is clicked/activated. The page
   * scrolls/centers the PDF on the first id. Omitted when rows aren't
   * interactive (e.g. no document selected).
   */
  onSelectFields?: (fieldIds: string[]) => void;
}

/**
 * Right-rail card of document-level suggestions for the selected document.
 * These are validation findings presented in softer, agent-facing "suggestion"
 * framing. Each row shows a warning icon, the message, and a "Page N • Ignore"
 * subrow. Renders nothing when there are no suggestions.
 *
 * Each row with field ids is clickable: it calls `onSelectFields(row.fieldIds)`
 * so the consumer can scroll/center the PDF on the field. The per-row "Ignore"
 * is a visual-only placeholder for now — the dispute / resubmit / hide actions
 * land in a later phase.
 */
export const DocumentSuggestionsList: FC<DocumentSuggestionsListProps> = ({
  suggestions,
  onSelectFields,
}) => {
  if (suggestions.length === 0) {
    return null;
  }

  const heading =
    suggestions.length === 1
      ? '1 document suggestion'
      : `${suggestions.length} document suggestions`;

  return (
    <Box
      bg="whiteAlpha.50"
      borderColor="whiteAlpha.100"
      borderRadius="10px"
      borderWidth={1}
      p={5}
    >
      <VStack align="stretch" spacing={2}>
        <Heading
          color="compliance.status.warning"
          fontSize="lg"
          fontWeight="normal"
          size="headline"
        >
          {heading}
        </Heading>

        <VStack align="stretch" spacing={1}>
          {suggestions.map((suggestion, index) => {
            const pageLabel = formatPageList(suggestion.pageNumbers ?? []);
            const fieldIds = suggestion.fieldIds ?? [];
            const isClickable = onSelectFields != null && fieldIds.length > 0;

            const handleSelect = (): void => {
              if (isClickable) {
                onSelectFields?.(fieldIds);
              }
            };

            const handleKeyDown = (event: KeyboardEvent): void => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleSelect();
              }
            };

            return (
              <Flex
                key={suggestion.ruleUuid ?? `document-suggestion-${index}`}
                _hover={isClickable ? { bg: 'whiteAlpha.100' } : undefined}
                align="flex-start"
                borderRadius="6px"
                cursor={isClickable ? 'pointer' : undefined}
                gap={2}
                mx={-2}
                px={2}
                py={1}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onClick={isClickable ? handleSelect : undefined}
                onKeyDown={isClickable ? handleKeyDown : undefined}
              >
                <Icon
                  as={TriangleAlertIcon}
                  boxSize="16px"
                  color="compliance.status.warning"
                  flexShrink={0}
                  mt={1}
                />
                <Flex direction="column" gap={1} minW={0}>
                  <Text color="compliance.text.primary" fontSize="sm">
                    {suggestion.message}
                  </Text>
                  <Flex color="whiteAlpha.600" fontSize="sm" gap={1.5}>
                    {pageLabel && <Text>{pageLabel}</Text>}
                    {pageLabel && <Text aria-hidden>•</Text>}
                    {/* Visual-only placeholder; the ignore/hide action is a
                        later phase. stopPropagation so it doesn't also trigger
                        the row's scroll-to-field select. */}
                    <Text
                      _hover={{ color: 'compliance.text.secondary' }}
                      as="span"
                      cursor="pointer"
                      textDecoration="underline"
                      onClick={(event) => event.stopPropagation()}
                    >
                      Ignore
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
            );
          })}
        </VStack>
      </VStack>
    </Box>
  );
};
